import React, { useMemo, useState } from "react";
import { Table, Button, Descriptions, Popconfirm, Tooltip, message } from "antd";
import { Link } from "react-router-dom";
import { DownloadOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import TableToolbar from "./TableToolbar";
import TablePagination from "./TablePagination";
import {
  useContainerWidth,
  splitColumns,
  useStickyHeaderOffset,
} from "./useResponsiveColumns";
import { downloadCsv, todayStamp } from "./exportCsv";

// تحت هذا العرض تصبح أزرار الصف أيقونات في عمود واحد
const NARROW_CONTAINER = 620;
// عرض عمود التوسيع (+)
const EXPAND_COLUMN_WIDTH = 48;

/** عمود "تعديل" الموحّد لكل الجداول */
export const editAction = (to, { title = "تعديل" } = {}) => ({
  key: "edit",
  title,
  width: 80,
  render: (record) => (
    <button className="btn btn-outline-warning btn-sm p-0">
      <Link
        className="color-inherit text-decoration-none py-1 px-2 d-block"
        to={to(record)}
      >
        {title}
      </Link>
    </button>
  ),
  compactRender: (record) => (
    <Tooltip title={title}>
      <Link to={to(record)} aria-label={title}>
        <Button size="small" icon={<EditOutlined />} />
      </Link>
    </Tooltip>
  ),
});

/** عمود "حذف" الموحّد مع تأكيد */
export const deleteAction = (onConfirm, { title = "حذف" } = {}) => {
  const wrap = (record, children) => (
    <Popconfirm
      title="حذف العنصر"
      description="هل أنت متأكد من حذف هذا العنصر؟"
      onConfirm={() => onConfirm(record)}
      okText="نعم"
      cancelText="لا"
    >
      {children}
    </Popconfirm>
  );

  return {
    key: "delete",
    title,
    width: 80,
    render: (record) =>
      wrap(
        record,
        <Button danger size="small">
          {title}
        </Button>,
      ),
    compactRender: (record) =>
      wrap(
        record,
        <Button danger size="small" icon={<DeleteOutlined />} aria-label={title} />,
      ),
  };
};

/**
 * جدول لوحة التحكم الموحّد: شريط أدوات + جدول متجاوب بلا سكرول داخلي
 * + صف موسّع للأعمدة التي لا تتّسع + تذييل بمعلومات الأسطر.
 *
 * @param {object} table نتيجة useTableData
 * @param {Array} fields تعريف الأعمدة — مصدر واحد للجدول والصف الموسّع والتصدير
 *   { key, title, width, priority, value(record), render?(record), sortable? }
 * @param {Array} [actions] أعمدة الإجراءات في نهاية الجدول (editAction/deleteAction/مخصّص)
 * @param {React.ReactNode} [toolbarActions] أزرار أعلى الجدول (إضافة، فلاتر...)
 * @param {string} [exportName] اسم ملف التصدير — بدونه لا يظهر زر التصدير
 */
const DataTable = ({
  table,
  fields,
  actions = [],
  toolbarActions = null,
  exportName,
  itemLabel = "سجل",
  searchPlaceholder = "بحث...",
  rowKey = "id",
  emptyText = "لا يوجد عناصر",
  showSearch = true,
  size = "middle",
}) => {
  const [exporting, setExporting] = useState(false);
  const { containerRef, containerWidth } = useContainerWidth();
  const stickyOffset = useStickyHeaderOffset();

  // على الحاويات الضيقة (جوال) نُدمج أزرار الصف في عمود واحد بأيقونات،
  // وإلا استهلكت الأزرار النصية معظم العرض ولم يبقَ مكان لأي بيانات
  const compactActions = containerWidth > 0 && containerWidth < NARROW_CONTAINER;
  const actionsWidth = actions.length
    ? compactActions
      ? Math.max(56, actions.length * 42)
      : actions.reduce((sum, action) => sum + (action.width ?? 80), 0)
    : 0;

  const { visibleFields, hiddenFields, tight } = useMemo(
    // نحجز عرض عمود (+) وأعمدة الإجراءات قبل توزيع باقي الأعمدة
    () =>
      splitColumns(fields, containerWidth, {
        reservedWidth: actionsWidth + EXPAND_COLUMN_WIDTH,
        minVisible: 2,
      }),
    [fields, containerWidth, actionsWidth],
  );

  const { sort } = table;

  const columns = useMemo(() => {
    const list = visibleFields.map((field) => ({
      key: field.key,
      title: field.title,
      // عند ضيق المساحة نترك المتصفح يوزّع العرض مع قص النص بدل السكرول الأفقي
      width: tight ? undefined : field.width,
      ellipsis: field.ellipsis ?? true,
      sorter: field.sortable === false ? undefined : true,
      sortOrder: sort.field === field.key ? sort.order : null,
      showSorterTooltip: false,
      align: field.align,
      render: (_, record) => {
        if (field.render) return field.render(record);

        const value = field.value(record);

        return value === null || value === undefined || value === "" ? "-" : value;
      },
    }));

    if (!actions.length) return list;

    if (compactActions) {
      list.push({
        key: "actions",
        title: "",
        width: tight ? undefined : actionsWidth,
        render: (_, record) => (
          <div className="table-row-actions">
            {actions.map((action) => (
              <React.Fragment key={action.key}>
                {(action.compactRender ?? action.render)(record)}
              </React.Fragment>
            ))}
          </div>
        ),
      });

      return list;
    }

    actions.forEach((action) => {
      list.push({
        key: action.key,
        title: action.title,
        width: tight ? undefined : action.width ?? 80,
        render: (_, record) => action.render(record),
      });
    });

    return list;
  }, [visibleFields, tight, sort, actions, compactActions, actionsWidth]);

  const expandable = useMemo(() => {
    if (!hiddenFields.length) return undefined;

    return {
      // عرض صريح لعمود (+) حتى يتطابق رأس الجدول مع جسمه تماماً
      columnWidth: EXPAND_COLUMN_WIDTH,
      rowExpandable: () => true,
      expandedRowRender: (record) => (
        <Descriptions
          className="table-expanded-details"
          size="small"
          bordered
          column={{ xs: 1, sm: 2, xl: 3 }}
          items={hiddenFields.map((field) => ({
            key: field.key,
            label: field.title,
            children: field.render ? field.render(record) : field.value(record) || "-",
          }))}
        />
      ),
    };
  }, [hiddenFields]);

  /** يسحب كل الصفحات (مع البحث والترتيب الحاليين) ثم ينزّلها CSV بكل الأعمدة */
  const handleExport = async () => {
    setExporting(true);

    try {
      const allRows = await table.fetchAllRows();

      if (!allRows.length) {
        message.warning("لا توجد بيانات للتصدير");

        return;
      }

      downloadCsv(
        `${exportName}-${todayStamp()}`,
        fields.map((field) => field.title),
        allRows.map((record) => fields.map((field) => field.value(record) ?? "")),
      );
      message.success(`تم تصدير ${allRows.length} سجلاً`);
    } catch (error) {
      message.error("تعذّر تصدير البيانات");
    } finally {
      setExporting(false);
    }
  };

  const handleTableChange = (_pagination, _filters, sorter) =>
    table.setSort({ field: sorter?.columnKey ?? null, order: sorter?.order ?? null });

  return (
    <div className="row">
      {(showSearch || toolbarActions || exportName) && (
        <div className="col-12">
          <TableToolbar
            value={table.search}
            onSearch={table.setSearch}
            placeholder={searchPlaceholder}
            showSearch={showSearch}
            actions={
              (exportName || toolbarActions) && (
                <>
                  {exportName && (
                    <Button
                      icon={<DownloadOutlined />}
                      onClick={handleExport}
                      loading={exporting}
                    >
                      تصدير Excel
                    </Button>
                  )}
                  {toolbarActions}
                </>
              )
            }
          />
        </div>
      )}

      <div className="col-12 grid-margin stretch-card">
        <div className="w-100" ref={containerRef}>
          <Table
            className="responsive-table"
            locale={{ emptyText }}
            loading={table.loading}
            pagination={false}
            rowKey={rowKey}
            columns={columns}
            dataSource={table.rows}
            expandable={expandable}
            onChange={handleTableChange}
            // السكرول على الصفحة كاملة، ورأس الجدول يلتصق تحت الشريط العلوي
            sticky={{ offsetHeader: stickyOffset }}
            size={size}
          />
          <TablePagination
            current={table.page}
            pageSize={table.meta.perPage}
            total={table.meta.total}
            rangeFrom={table.meta.from}
            rangeTo={table.meta.to}
            loading={table.loading}
            itemLabel={itemLabel}
            onChange={table.setPage}
          />
        </div>
      </div>
    </div>
  );
};

export default DataTable;
